import React, { useEffect, useState } from 'react';

export default function Preloader({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [countNum, setCountNum] = useState(0);
  const [exiting,  setExiting]  = useState(false);
  const [done,     setDone]     = useState(false);

  useEffect(() => {
    const DURATION = 3200; // ~3.2s fill
    const HOLD     = 400;
    const EXIT     = 650;

    let startTs = null;
    let rafId;

    const tick = (ts) => {
      if (!startTs) startTs = ts;
      const elapsed = ts - startTs;
      const linear  = Math.min(elapsed / DURATION, 1);
      // ease-in-out cubic
      const eased = linear < 0.5
        ? 2 * linear * linear
        : 1 - Math.pow(-2 * linear + 2, 2) / 2;

      setProgress(eased * 100);
      setCountNum(Math.round(eased * 100));

      if (linear < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setTimeout(() => {
          setExiting(true);
          setTimeout(() => {
            setDone(true);
            onComplete?.();
          }, EXIT);
        }, HOLD);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [onComplete]);

  if (done) return null;

  return (
    <div style={{
      position:       'fixed',
      inset:          0,
      zIndex:         99999,
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      background:     '#07111f',
      transition:     exiting
        ? 'opacity 0.65s cubic-bezier(0.4,0,0.2,1), transform 0.65s cubic-bezier(0.4,0,0.2,1)'
        : 'none',
      opacity:        exiting ? 0   : 1,
      transform:      exiting ? 'scale(1.05)' : 'scale(1)',
      pointerEvents:  exiting ? 'none' : 'all',
      overflow:       'hidden',
    }}>

      {/* ── Hex grid bg ── */}
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', opacity:0.05 }}>
        <defs>
          <pattern id="pl-hex" x="0" y="0" width="56" height="48" patternUnits="userSpaceOnUse">
            <polygon points="28,2 54,16 54,32 28,46 2,32 2,16"
              fill="none" stroke="#2eb4b4" strokeWidth="0.8"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#pl-hex)"/>
      </svg>

      {/* ── Ambient glow ── */}
      <div style={{
        position:     'absolute',
        width:        700,
        height:       700,
        borderRadius: '50%',
        background:   'radial-gradient(circle, rgba(46,180,180,0.1) 0%, rgba(23,79,154,0.06) 40%, transparent 70%)',
        top:          '50%',
        left:         '50%',
        transform:    'translate(-50%, -50%)',
        animation:    'pl-breathe 3s ease-in-out infinite',
        pointerEvents:'none',
      }}/>

      {/* ── DNA Helix + orbit ── */}
      <div style={{ position:'relative', marginBottom: 36, width:72, height:116 }}>

        <svg width="72" height="116" viewBox="0 0 72 116" style={{ overflow:'visible', display:'block' }}>
          {/* Left strand */}
          <path d="M18,8 C18,28 54,28 54,48 C54,68 18,68 18,88 C18,108 54,108 54,128"
            fill="none" stroke="#2eb4b4" strokeWidth="2.5" strokeLinecap="round"
            style={{ strokeDasharray:320, strokeDashoffset:320,
                     animation:'pl-draw 1.4s ease 0.3s forwards' }}/>
          {/* Right strand */}
          <path d="M54,8 C54,28 18,28 18,48 C18,68 54,68 54,88 C54,108 18,108 18,128"
            fill="none" stroke="#174f9a" strokeWidth="2.5" strokeLinecap="round"
            style={{ strokeDasharray:320, strokeDashoffset:320,
                     animation:'pl-draw 1.4s ease 0.5s forwards' }}/>
          {/* Rungs */}
          {[20, 34, 48, 62, 76, 90, 104].map((y, i) => (
            <React.Fragment key={y}>
              <line x1="18" y1={y} x2="54" y2={y}
                stroke="rgba(46,180,180,0.45)" strokeWidth="1.8" strokeLinecap="round"
                style={{ animation:`pl-rung 0.35s ease ${0.7 + i*0.1}s both` }}/>
              <circle cx="18" cy={y} r="3.5" fill="#2eb4b4"
                style={{ animation:`pl-dot 0.3s cubic-bezier(.22,1,.36,1) ${0.72 + i*0.1}s both`, opacity:0 }}/>
              <circle cx="54" cy={y} r="3.5" fill="#174f9a"
                style={{ animation:`pl-dot 0.3s cubic-bezier(.22,1,.36,1) ${0.77 + i*0.1}s both`, opacity:0 }}/>
            </React.Fragment>
          ))}
        </svg>

        {/* ── Orbiting pill — fixed orbit ── */}
        {/*
          The trick: a square div centred on the helix acts as the "arm".
          We rotate the arm, then counter-rotate the pill so it stays upright.
          orbit-arm spins 360°; orbit-pill counter-spins -360°.
        */}
        <div className="orbit-arm" style={{
          position:       'absolute',
          top:            '50%',
          left:           '50%',
          width:          0,
          height:         0,
          transformOrigin:'0 0',
          animation:      'orbit-arm 2.8s linear 0.5s infinite',
        }}>
          {/* The pill is placed at radius 52px above the origin */}
          <div style={{
            position:     'absolute',
            left:         -14,       // half pill width (28/2)
            top:          -52,       // radius
            width:        28,
            height:       11,
            borderRadius: 99,
            background:   'linear-gradient(to right, #2eb4b4 50%, rgba(255,255,255,0.9) 50%)',
            boxShadow:    '0 0 14px rgba(46,180,180,0.75)',
            border:       '1px solid rgba(255,255,255,0.25)',
            animation:    'orbit-pill-counter 2.8s linear 0.5s infinite',
          }}/>
        </div>

        {/* Pulse rings around helix */}
        <div style={{
          position:     'absolute',
          top:          '50%',
          left:         '50%',
          transform:    'translate(-50%,-50%)',
          width:        130,
          height:       130,
          borderRadius: '50%',
          border:       '1px solid rgba(46,180,180,0.2)',
          animation:    'pl-pulse-ring 2.4s ease-out 1s infinite',
          pointerEvents:'none',
        }}/>
        <div style={{
          position:     'absolute',
          top:          '50%',
          left:         '50%',
          transform:    'translate(-50%,-50%)',
          width:        130,
          height:       130,
          borderRadius: '50%',
          border:       '1px solid rgba(46,180,180,0.15)',
          animation:    'pl-pulse-ring 2.4s ease-out 1.8s infinite',
          pointerEvents:'none',
        }}/>
      </div>

      {/* ── Brand name ── */}
      <div style={{ display:'flex', gap:1, marginBottom:10, overflow:'hidden' }}>
        {'BLEEN'.split('').map((ch, i) => (
          <span key={i} style={{
            display:     'inline-block',
            fontSize:    'clamp(40px,7vw,58px)',
            fontWeight:  900,
            fontFamily:  "'DM Sans', sans-serif",
            color:       '#fff',
            letterSpacing:'0.14em',
            animation:   `pl-letter 0.55s cubic-bezier(.22,1,.36,1) ${0.9 + i*0.08}s both`,
          }}>{ch}</span>
        ))}
      </div>

      <p style={{
        fontSize:      11,
        fontWeight:    700,
        letterSpacing: '0.28em',
        color:         'rgba(255,255,255,0.35)',
        textTransform: 'uppercase',
        marginBottom:  52,
        animation:     'pl-fade 0.6s ease 1.5s both',
        opacity:       0,
      }}>
        Healthcare · Quality · Trust
      </p>

      {/* ── Progress track ── */}
      <div style={{ width:'min(300px,64vw)', position:'relative' }}>
        <div style={{
          fontSize:    12,
          fontWeight:  800,
          fontFamily:  "'DM Mono', 'Courier New', monospace",
          color:       '#2eb4b4',
          letterSpacing:'0.04em',
          textAlign:   'right',
          marginBottom:8,
        }}>
          {String(countNum).padStart(3,'0')}%
        </div>

        <div style={{
          height:       2,
          background:   'rgba(255,255,255,0.08)',
          borderRadius: 99,
          overflow:     'hidden',
          position:     'relative',
        }}>
          <div style={{
            position:     'absolute',
            inset:        0,
            right:        `${100 - progress}%`,
            background:   'linear-gradient(to right, #2eb4b4, #174f9a)',
            borderRadius: 99,
            boxShadow:    '0 0 8px rgba(46,180,180,0.6)',
            transition:   'right 0.06s linear',
          }}/>
          {/* Glowing head */}
          <div style={{
            position:   'absolute',
            top:        '50%',
            left:       `${progress}%`,
            transform:  'translate(-50%,-50%)',
            width:      6,
            height:     6,
            borderRadius:'50%',
            background: '#fff',
            boxShadow:  '0 0 10px 2px rgba(46,180,180,0.9)',
            opacity:    progress > 1 && progress < 99 ? 1 : 0,
            transition: 'left 0.06s linear, opacity 0.2s',
          }}/>
        </div>

        {/* Step markers */}
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
          {['Initializing','Loading','Ready'].map((label, i) => {
            const thresh = [0, 50, 100][i];
            const active = progress >= thresh;
            return (
              <span key={label} style={{
                fontSize:      9,
                fontWeight:    700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color:         active ? '#2eb4b4' : 'rgba(255,255,255,0.2)',
                transition:    'color 0.4s ease',
              }}>{label}</span>
            );
          })}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@700;800;900&family=DM+Mono:wght@500&display=swap');

        @keyframes pl-draw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes pl-rung {
          from { opacity:0; transform:scaleX(0); transform-origin:center; }
          to   { opacity:1; transform:scaleX(1); }
        }
        @keyframes pl-dot {
          0%   { opacity:0; r:0; }
          60%  { opacity:1; r:5; }
          100% { opacity:1; r:3.5; }
        }
        @keyframes pl-letter {
          from { opacity:0; transform:translateY(44px) skewY(6deg); }
          to   { opacity:1; transform:translateY(0) skewY(0deg); }
        }
        @keyframes pl-fade {
          from { opacity:0; transform:translateY(8px); }
          to   { opacity:1; transform:translateY(0); }
        }

        /* ── Correct orbit: arm rotates, pill counter-rotates to stay upright ── */
        @keyframes orbit-arm {
          from { transform: rotate(0deg);   }
          to   { transform: rotate(360deg); }
        }
        @keyframes orbit-pill-counter {
          from { transform: rotate(0deg);    }
          to   { transform: rotate(-360deg); }
        }

        @keyframes pl-breathe {
          0%,100% { opacity:1;   transform:translate(-50%,-50%) scale(1);    }
          50%     { opacity:0.6; transform:translate(-50%,-50%) scale(1.12); }
        }
        @keyframes pl-pulse-ring {
          0%   { transform:translate(-50%,-50%) scale(0.85); opacity:0.7; }
          100% { transform:translate(-50%,-50%) scale(2.2);  opacity:0;   }
        }
      `}</style>
    </div>
  );
}